import { Controller } from "@hotwired/stimulus"

// Renders a live preview of the provider search disclaimer in the
// configuration page. Watches the disclaimer, preview-text, and
// read-more-enabled inputs and re-renders whenever they change.
export default class extends Controller {
  static targets = ["output"]
  static values = {
    disclaimerField: String,
    previewTextField: String,
    readMoreField: String
  }

  connect() {
    this.disclaimerInput = document.getElementById(this.disclaimerFieldValue)
    this.previewTextInput = document.getElementById(this.previewTextFieldValue)
    this.readMoreInput = document.getElementById(this.readMoreFieldValue)

    this._listen(this.disclaimerInput)
    this._listen(this.previewTextInput)
    this._listen(this.readMoreInput)

    this.render()
  }

  disconnect() {
    this._unlisten(this.disclaimerInput)
    this._unlisten(this.previewTextInput)
    this._unlisten(this.readMoreInput)
  }

  render() {
    const disclaimer = this.disclaimerInput?.value || ""
    const previewText = this.previewTextInput?.value || ""
    const readMoreEnabled = this.readMoreInput?.value === "true"

    if (!disclaimer.trim()) {
      this.outputTarget.innerHTML = '<span class="text-support-dark-400 text-xs italic">Enter disclaimer text above to see a preview.</span>'
      return
    }

    const sanitized = this._sanitize(disclaimer)
    const sanitizedPreview = this._sanitize(previewText)

    if (readMoreEnabled) {
      const preview = sanitizedPreview || this._truncate(this._stripTags(disclaimer), 200)
      this.outputTarget.innerHTML = `
        <div data-disclaimer-preview-state="collapsed">
          <div class="text-xs text-support-dark-400">${preview}</div>
          <a href="#" class="text-link text-xs mt-1 inline-block" data-action="click->disclaimer-preview#expand">Read more</a>
        </div>
        <div data-disclaimer-preview-state="expanded" class="hidden">
          <div class="text-xs text-support-dark-400">${sanitized}</div>
          <a href="#" class="text-link text-xs mt-1 inline-block" data-action="click->disclaimer-preview#collapse">Read less</a>
        </div>
      `
    } else {
      this.outputTarget.innerHTML = `<div class="text-xs text-support-dark-400">${sanitized}</div>`
    }
  }

  expand(event) {
    event.preventDefault()
    const collapsed = this.outputTarget.querySelector('[data-disclaimer-preview-state="collapsed"]')
    const expanded = this.outputTarget.querySelector('[data-disclaimer-preview-state="expanded"]')
    if (collapsed) collapsed.classList.add("hidden")
    if (expanded) expanded.classList.remove("hidden")
  }

  collapse(event) {
    event.preventDefault()
    const collapsed = this.outputTarget.querySelector('[data-disclaimer-preview-state="collapsed"]')
    const expanded = this.outputTarget.querySelector('[data-disclaimer-preview-state="expanded"]')
    if (expanded) expanded.classList.add("hidden")
    if (collapsed) collapsed.classList.remove("hidden")
  }

  // --- private ---

  _listen(el) {
    if (el) el.addEventListener("input", this._onInput)
    if (el) el.addEventListener("change", this._onInput)
  }

  _unlisten(el) {
    if (el) el.removeEventListener("input", this._onInput)
    if (el) el.removeEventListener("change", this._onInput)
  }

  _onInput = () => { this.render() }

  _sanitize(html) {
    const allowed = ["a", "p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "span", "div", "h1", "h2", "h3", "h4", "h5", "h6"]
    const allowedAttrs = ["href", "target", "rel", "class", "id", "style"]
    const doc = new DOMParser().parseFromString(html, "text/html")
    return this._sanitizeNode(doc.body, allowed, allowedAttrs)
  }

  _sanitizeNode(node, allowedTags, allowedAttrs) {
    let out = ""
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += this._escapeText(child.textContent)
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (allowedTags.includes(tag)) {
          let attrs = ""
          for (const attr of child.attributes) {
            if (allowedAttrs.includes(attr.name.toLowerCase())) {
              attrs += ` ${attr.name}="${this._escapeAttr(attr.value)}"`
            }
          }
          if (tag === "a") attrs += ' class="text-link"'
          out += `<${tag}${attrs}>${this._sanitizeNode(child, allowedTags, allowedAttrs)}</${tag}>`
        } else {
          out += this._sanitizeNode(child, allowedTags, allowedAttrs)
        }
      }
    }
    return out
  }

  _escapeText(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  _escapeAttr(str) {
    return str.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  _stripTags(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    return doc.body.textContent || ""
  }

  _truncate(str, len) {
    if (str.length <= len) return this._escapeText(str)
    return this._escapeText(str.substring(0, len)) + "…"
  }
}
